import React, { useState } from 'react';
import './PrizeTierBuilder.css';

/**
 * PrizeTierBuilder Component
 *
 * Allows users to configure multiple winners with flexible prize tiers.
 * Supports individual ranks (1st, 2nd, 3rd) or rank ranges (4th-10th).
 */
function PrizeTierBuilder({ tiers, onChange, totalWinners, onTotalWinnersChange }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);

  const templates = {
    'single-winner': {
      name: 'Single Winner',
      description: 'Traditional one winner takes all',
      tiers: [
        { tierName: 'Winner', tierRankStart: 1, tierRankEnd: 1, prizeName: '', prizeValue: '', displayOrder: 1 }
      ]
    },
    '3-winner-tiers': {
      name: '3 Winners (Tiered)',
      description: '1st, 2nd, and 3rd place prizes',
      tiers: [
        { tierName: '1st Place', tierRankStart: 1, tierRankEnd: 1, prizeName: '', prizeValue: '', displayOrder: 1 },
        { tierName: '2nd Place', tierRankStart: 2, tierRankEnd: 2, prizeName: '', prizeValue: '', displayOrder: 2 },
        { tierName: '3rd Place', tierRankStart: 3, tierRankEnd: 3, prizeName: '', prizeValue: '', displayOrder: 3 }
      ]
    },
    '10-winner-grouped': {
      name: '10 Winners (Grouped)',
      description: 'Grand prize, runner-ups, and honorable mentions',
      tiers: [
        { tierName: 'Grand Prize', tierRankStart: 1, tierRankEnd: 1, prizeName: '', prizeValue: '', displayOrder: 1 },
        { tierName: 'Runner-Up (2nd-3rd)', tierRankStart: 2, tierRankEnd: 3, prizeName: '', prizeValue: '', displayOrder: 2 },
        { tierName: 'Honorable Mention (4th-10th)', tierRankStart: 4, tierRankEnd: 10, prizeName: '', prizeValue: '', displayOrder: 3 }
      ]
    },
    '50-winner-large': {
      name: '50 Winners (Large Raffle)',
      description: 'Multi-tier prize structure for big raffles',
      tiers: [
        { tierName: '1st Place - Grand Prize', tierRankStart: 1, tierRankEnd: 1, prizeName: '', prizeValue: '', displayOrder: 1 },
        { tierName: '2nd Place', tierRankStart: 2, tierRankEnd: 2, prizeName: '', prizeValue: '', displayOrder: 2 },
        { tierName: '3rd Place', tierRankStart: 3, tierRankEnd: 3, prizeName: '', prizeValue: '', displayOrder: 3 },
        { tierName: 'Top 10 (4th-10th)', tierRankStart: 4, tierRankEnd: 10, prizeName: '', prizeValue: '', displayOrder: 4 },
        { tierName: 'Top 20 (11th-20th)', tierRankStart: 11, tierRankEnd: 20, prizeName: '', prizeValue: '', displayOrder: 5 },
        { tierName: 'Remaining (21st-50th)', tierRankStart: 21, tierRankEnd: 50, prizeName: '', prizeValue: '', displayOrder: 6 }
      ]
    }
  };

  const applyTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      onChange(template.tiers);
      const maxRank = Math.max(...template.tiers.map(t => t.tierRankEnd));
      onTotalWinnersChange(maxRank);
      setSelectedTemplate(templateKey);
      setShowTemplates(false);
    }
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const newRankStart = lastTier ? lastTier.tierRankEnd + 1 : 1;

    onChange([
      ...tiers,
      {
        tierName: '',
        tierRankStart: newRankStart,
        tierRankEnd: newRankStart,
        prizeName: '',
        prizeDescription: '',
        prizeValue: '',
        displayOrder: tiers.length + 1
      }
    ]);
  };

  const removeTier = (index) => {
    if (tiers.length > 1) {
      const updatedTiers = tiers.filter((_, i) => i !== index);
      // Reorder
      updatedTiers.forEach((tier, i) => {
        tier.displayOrder = i + 1;
      });
      onChange(updatedTiers);
    }
  };

  const updateTier = (index, field, value) => {
    const updatedTiers = [...tiers];
    updatedTiers[index][field] = value;

    // Auto-update total winners when tier_rank_end changes
    if (field === 'tierRankEnd') {
      const maxRank = Math.max(...updatedTiers.map(t => parseInt(t.tierRankEnd) || 1));
      onTotalWinnersChange(maxRank);
    }

    onChange(updatedTiers);
  };

  const getTierRangeDisplay = (tier) => {
    if (tier.tierRankStart === tier.tierRankEnd) {
      return `${tier.tierRankStart}${getOrdinalSuffix(tier.tierRankStart)}`;
    }
    return `${tier.tierRankStart}${getOrdinalSuffix(tier.tierRankStart)} - ${tier.tierRankEnd}${getOrdinalSuffix(tier.tierRankEnd)}`;
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  return (
    <div className="prize-tier-builder">
      <div className="prize-tier-header">
        <h3>🏆 Prize Tiers Configuration</h3>
        <p className="prize-tier-description">
          Configure multiple winners with different prizes. You can set individual ranks (1st, 2nd, 3rd)
          or rank ranges (4th-10th get same prize).
        </p>
      </div>

      <div className="template-selector">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowTemplates(!showTemplates)}
        >
          📋 Use Template
        </button>

        {showTemplates && (
          <div className="template-dropdown">
            {Object.entries(templates).map(([key, template]) => (
              <div
                key={key}
                className={`template-option ${selectedTemplate === key ? 'selected' : ''}`}
                onClick={() => applyTemplate(key)}
              >
                <strong>{template.name}</strong>
                <p>{template.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="total-winners-display">
        <span className="winners-badge">Total Winners: {totalWinners}</span>
      </div>

      <div className="prize-tiers-list">
        {tiers.map((tier, index) => (
          <div key={index} className="prize-tier-card">
            <div className="tier-card-header">
              <span className="tier-rank-badge">{getTierRangeDisplay(tier)}</span>
              {tiers.length > 1 && (
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => removeTier(index)}
                  title="Remove this tier"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="tier-card-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Tier Name</label>
                  <input
                    type="text"
                    placeholder="e.g., 1st Place, Grand Prize"
                    value={tier.tierName || ''}
                    onChange={(e) => updateTier(index, 'tierName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rank Start</label>
                  <input
                    type="number"
                    min="1"
                    value={tier.tierRankStart || ''}
                    onChange={(e) => updateTier(index, 'tierRankStart', parseInt(e.target.value))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Rank End</label>
                  <input
                    type="number"
                    min={tier.tierRankStart || 1}
                    value={tier.tierRankEnd || ''}
                    onChange={(e) => updateTier(index, 'tierRankEnd', parseInt(e.target.value))}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prize Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., iPad Pro 12.9 inch, $500 Cash"
                    value={tier.prizeName || ''}
                    onChange={(e) => updateTier(index, 'prizeName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prize Value ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={tier.prizeValue || ''}
                    onChange={(e) => updateTier(index, 'prizeValue', parseFloat(e.target.value))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Prize Description</label>
                  <textarea
                    placeholder="Detailed description of the prize..."
                    value={tier.prizeDescription || ''}
                    onChange={(e) => updateTier(index, 'prizeDescription', e.target.value)}
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn-add-tier"
        onClick={addTier}
      >
        + Add Prize Tier
      </button>
    </div>
  );
}

export default PrizeTierBuilder;
